import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { createHttpClient } from '@adapters/HttpClient';
import { Button } from '@components/ui/button/Button';
import { Badge } from '@shared/components/ui/badge';
import { DynamicCombobox } from '@shared/components/ui/DynamicCombobox';
import { Label } from '@shared/components/ui/label';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { Spinner } from '@shared/components/ui/Spinner';
import { apiClientWH } from '@shared/services';
import { formatDateTime } from '@utils/FormatUtils';
import { TeamsApiAdapter } from '@modules/Invoices/Invoices/List/adapters/TeamsApiAdapter';
import { usePopupEvents } from '../../hooks/usePopupEvents';
import type { Popup } from '../../models/Popup';
import type { PopupEvent, PopupEventType } from '../../models/PopupEvent';
import { getPopupLocalizedContent } from '../../utils/popupLocale';

export interface PopupEventsModalRef {
  open: (popup: Popup) => void;
  close: () => void;
}

type TypeFilter = 'all' | PopupEventType;

const ALL_TYPE_VALUE = 'all';
const ALL_TEAM_VALUE = '';

function PopupEventRow({ event, isLast }: { event: PopupEvent; isLast: boolean }) {
  const { t } = useTranslation('popups');
  const typeLabel = t(`events.type.${event.type}`);
  const isRead = event.type === 'read';

  return (
    <div className={`px-4 py-3 ${isLast ? '' : 'border-b border-border'}`} data-testid={`popup-event-row-${event.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-medium text-foreground">{formatDateTime(event.occurred_at)}</p>
          <p className="text-sm text-muted-foreground">
            {event.team?.name ?? '-'} · {event.viewer?.name ?? '-'}
          </p>
          <p className="truncate text-xs text-muted-foreground" title={event.client?.user_agent}>
            {event.client?.ip ?? '-'}
            {event.client?.host ? ` · ${event.client.host}` : ''}
          </p>
        </div>
        <Badge variant={isRead ? 'secondary' : 'default'}>{typeLabel}</Badge>
      </div>
    </div>
  );
}

export const PopupEventsModal = forwardRef<PopupEventsModalRef>((_, ref) => {
  const { t, i18n } = useTranslation('popups');
  const modalRef = useRef<ModalCenterRef>(null);
  const [popup, setPopup] = useState<Popup | null>(null);
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>(ALL_TYPE_VALUE);
  const [teamId, setTeamId] = useState(ALL_TEAM_VALUE);

  const teamsAdapter = useMemo(() => new TeamsApiAdapter(createHttpClient(apiClientWH)), []);

  const searchTeams = useCallback(
    async (query: string) => {
      const teams = await teamsAdapter.getTeamsChild();
      const searchLower = query.trim().toLowerCase();

      return teams
        .filter((team) => !searchLower || team.name.toLowerCase().includes(searchLower))
        .map((team) => ({
          label: team.name,
          value: team.id.toString(),
        }));
    },
    [teamsAdapter],
  );

  const resolveTeamOption = useCallback(
    async (value: string) => {
      if (!value) return null;
      const teams = await teamsAdapter.getTeamsChild();
      const team = teams.find((item) => item.id.toString() === value);
      return team ? { label: team.name, value: team.id.toString() } : null;
    },
    [teamsAdapter],
  );

  const { events, meta, isLoading, isFetching } = usePopupEvents(popup?.id ?? null, {
    page,
    perPage: 15,
    type: typeFilter === ALL_TYPE_VALUE ? undefined : typeFilter,
    team_id: teamId ? Number(teamId) : undefined,
  });

  const popupTitle = popup ? getPopupLocalizedContent(popup, i18n.resolvedLanguage ?? i18n.language).title : '';

  useImperativeHandle(ref, () => ({
    open: (selectedPopup: Popup) => {
      setPopup(selectedPopup);
      setPage(1);
      setTypeFilter(ALL_TYPE_VALUE);
      setTeamId(ALL_TEAM_VALUE);
      modalRef.current?.openModal();
    },
    close: () => {
      modalRef.current?.closeModal();
    },
  }));

  const handleClose = () => {
    setPopup(null);
    setPage(1);
    setTypeFilter(ALL_TYPE_VALUE);
    setTeamId(ALL_TEAM_VALUE);
  };

  const handleTypeChange = (value: TypeFilter) => {
    setTypeFilter(value);
    setPage(1);
  };

  const handleTeamChange = (value: string) => {
    setTeamId(value);
    setPage(1);
  };

  const currentPage = meta?.currentPage ?? 1;
  const lastPage = meta?.lastPage ?? 1;
  const total = meta?.total ?? 0;
  const isListLoading = isLoading || isFetching;

  return (
    <ModalCenter
      ref={modalRef}
      title={t('events.modal.title')}
      type="default"
      width="xl"
      classNameBody="max-h-[calc(100vh-120px)] overflow-hidden flex flex-col"
      onCloseModal={handleClose}
      data-testid="popup-events-modal"
    >
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        {popupTitle ? (
          <p className="text-sm text-muted-foreground">{t('events.modal.subtitle', { title: popupTitle })}</p>
        ) : null}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="popup-events-type">{t('events.filters.type')}</Label>
            <Select value={typeFilter} onValueChange={(value) => handleTypeChange(value as TypeFilter)}>
              <SelectTrigger id="popup-events-type" data-testid="popup-events-type-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_TYPE_VALUE}>{t('events.filters.typeAll')}</SelectItem>
                <SelectItem value="shown">{t('events.type.shown')}</SelectItem>
                <SelectItem value="read">{t('events.type.read')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="popup-events-team">{t('events.filters.team')}</Label>
            <DynamicCombobox
              value={teamId}
              setValue={handleTeamChange}
              placeholder={t('events.filters.teamPlaceholder')}
              searchFn={searchTeams}
              resolveSelectedOption={resolveTeamOption}
              dataTestId="popup-events-team-filter"
              emptyMessage={t('events.filters.noTeams')}
            />
            {teamId ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-auto px-0 text-xs text-muted-foreground"
                onClick={() => handleTeamChange(ALL_TEAM_VALUE)}
              >
                {t('events.filters.clearTeam')}
              </Button>
            ) : null}
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-background">
          {isListLoading ? (
            <div className="flex min-h-[12rem] flex-1 items-center justify-center gap-3 py-10">
              <Spinner size="md" />
              <span className="text-sm text-muted-foreground">{t('events.loading')}</span>
            </div>
          ) : events.length === 0 ? (
            <div className="flex min-h-[12rem] flex-1 items-center justify-center px-6 py-10 text-center">
              <p className="text-sm text-muted-foreground">{t('events.empty')}</p>
            </div>
          ) : (
            <div className="min-h-0 overflow-y-auto">
              {events.map((event, index) => (
                <PopupEventRow key={event.id} event={event} isLast={index === events.length - 1} />
              ))}
            </div>
          )}
        </div>

        {total > 0 && (
          <div className="flex shrink-0 items-center justify-between gap-3 border-t border-border pt-4">
            <p className="text-xs text-muted-foreground sm:text-sm">
              {t('events.pagination', { page: currentPage, lastPage, total })}
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={currentPage <= 1 || isListLoading}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                data-testid="popup-events-prev-page"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden />
                {t('events.paginationPrev')}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={currentPage >= lastPage || isListLoading}
                onClick={() => setPage((prev) => prev + 1)}
                data-testid="popup-events-next-page"
              >
                {t('events.paginationNext')}
                <ChevronRight className="h-4 w-4" aria-hidden />
              </Button>
            </div>
          </div>
        )}
      </div>
    </ModalCenter>
  );
});

PopupEventsModal.displayName = 'PopupEventsModal';
